"use client";

import { useRef, useEffect, useState, useCallback } from "react";
import { motion, useInView } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Camera,
  Archive,
  MessageSquare,
  ShieldCheck,
  ArrowRight,
  Play,
} from "lucide-react";
import Link from "next/link";
import posthog from "posthog-js";

// --- INTERSECTION-OBSERVED COUNT-UP ---

function AnimatedCounter({
  target,
  duration = 1200,
  prefix = "",
  suffix = "",
  decimals = 0,
}: {
  target: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReduced) {
      setCount(target);
      return;
    }

    let start: number | null = null;
    let raf: number;
    const step = (ts: number) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(eased * target);
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, duration]);

  return (
    <span ref={ref}>
      {prefix}
      {count.toLocaleString("it-IT", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}
      {suffix}
    </span>
  );
}

// --- PERSONA CARDS DATA ---

const personas = [
  {
    icon: Archive,
    painPoint: "Il tuo archivio di 20 anni vale oro.",
    zeroEntryAngle:
      "Scatta una foto: KennyDB lo rende portatile, per sempre tuo, pronto da vendere quando vorrai.",
    label: "Super-Agente",
  },
  {
    icon: MessageSquare,
    painPoint: "WhatsApp + Excel = caos.",
    zeroEntryAngle:
      "Parla o fotografa. KennyDB scrive nel tuo CRM. Zero sforzo, massimo ordine.",
    label: "Agente Tradizionale",
  },
  {
    icon: ShieldCheck,
    painPoint: "Un errore di battitura = commissione persa.",
    zeroEntryAngle:
      "KennyDB scansiona e confronta al posto tuo. Non perderai nemmeno un centesimo.",
    label: "Agente Sotto Pressione",
  },
];

// --- BENCHMARKS ---

const benchmarks = [
  { value: 3.2, unit: "ore/settimana", label: "risparmiate", decimals: 1 },
  {
    value: 1500,
    unit: "",
    prefix: "€",
    label: "recuperati in una commissione",
    decimals: 0,
    href: "#social-proof",
  },
  { value: 0, unit: "campi da digitare", label: "", decimals: 0 },
];

// --- SPLIT SCREEN COMPARATOR ---

function PrimaDopoComparator() {
  const containerRef = useRef<HTMLDivElement>(null);
  const inView = useInView(containerRef, { once: true, amount: 0.5 });
  const [prefersReduced, setPrefersReduced] = useState(false);

  useEffect(() => {
    setPrefersReduced(
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }, []);

  return (
    <div ref={containerRef} className="relative w-full max-w-4xl mx-auto mt-12">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-0 md:divide-x md:divide-mocha-200">
        {/* PRIMA */}
        <motion.div
          initial={prefersReduced ? {} : { opacity: 0, x: -30 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="relative bg-mocha-100 rounded-xl md:rounded-r-none p-8 md:p-10 text-center min-h-[280px] flex flex-col items-center justify-center"
          aria-label="Prima — Excel e WhatsApp"
        >
          <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-mocha-500 mb-4">
            Prima
          </span>
          {/* Visual metaphor: stack of paper + keyboard */}
          <div className="relative w-24 h-24 mx-auto mb-6">
            <div className="absolute inset-0 bg-mocha-300 rounded-lg rotate-[-6deg]" />
            <div className="absolute inset-0 bg-mocha-200 rounded-lg rotate-[-2deg] flex items-center justify-center">
              <svg
                className="w-10 h-10 text-mocha-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
          </div>
          <p className="text-sm text-mocha-600 font-medium">
            Excel + WhatsApp
          </p>
          <p className="text-xs text-mocha-400 mt-1">
            2 ore di digitazione al giorno
          </p>
        </motion.div>

        {/* DOPO */}
        <motion.div
          initial={prefersReduced ? {} : { opacity: 0, x: 30 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.15 }}
          className="relative bg-gradient-to-br from-ruby-50 to-honey-50 rounded-xl md:rounded-l-none p-8 md:p-10 text-center min-h-[280px] flex flex-col items-center justify-center"
          aria-label="Dopo — KennyDB"
        >
          <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-ruby-600 mb-4">
            Dopo
          </span>
          {/* Visual metaphor: phone camera + check */}
          <div className="relative w-24 h-24 mx-auto mb-6">
            <div className="absolute inset-0 bg-white rounded-2xl shadow-lg flex items-center justify-center">
              <Camera className="w-10 h-10 text-ruby-600" aria-hidden="true" />
            </div>
            <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>
          <p className="text-sm text-ruby-700 font-semibold">KennyDB</p>
          <p className="text-xs text-mocha-500 mt-1">
            2 minuti, dati inseriti da soli
          </p>
        </motion.div>
      </div>

      {/* Bottom caption */}
      <p className="text-center text-sm text-mocha-500 mt-6 italic max-w-md mx-auto">
        Prima il tempo volava tra i tasti. Ora lo dedichi ai clienti.
      </p>
    </div>
  );
}

// --- DEMO MODAL ---

function DemoModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Demo video di KennyDB"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl rounded-2xl overflow-hidden bg-mocha-950 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/30 transition-colors"
          aria-label="Chiudi video"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <video
          className="aspect-video w-full"
          controls
          autoPlay
          src="/zero-data-entry.mp4"
        >
          Il tuo browser non supporta il tag video.
        </video>
      </div>
    </div>
  );
}

// --- MAIN SECTION ---

export function ZeroEntrySection() {
  const [demoOpen, setDemoOpen] = useState(false);

  const handleCTAClick = useCallback(() => {
    posthog.capture("cta_click", {
      section: "zero_entry",
      variant: "A",
    });
  }, []);

  return (
    <section
      id="zero-entry"
      className="py-20 md:py-32 px-4 sm:px-6 lg:px-8 bg-white"
      aria-labelledby="zero-entry-heading"
    >
      <div className="container mx-auto max-w-6xl">
        {/* ---- HEADLINE ---- */}
        <div className="text-center mb-4">
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-ruby-600 mb-4">
            Zero data-entry
          </span>
          <h2
            id="zero-entry-heading"
            className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-mocha-950 tracking-tight mb-4 max-w-xl mx-auto"
          >
            Ieri 2 ore.{" "}
            <span className="text-ruby-600">Oggi 2 minuti.</span>
          </h2>
          <p className="text-lg text-mocha-600 max-w-2xl mx-auto leading-relaxed">
            KennyDB cattura, interpreta e archivia i tuoi ordini, fatture e note
            provvigionali.{" "}
            <strong className="text-mocha-950">Tu controlli. Non digiti.</strong>
          </p>
        </div>

        {/* ---- PRIMA / DOPO COMPARATOR ---- */}
        <PrimaDopoComparator />

        {/* ---- PERSONA CARDS ---- */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-20">
          {personas.map((persona) => (
            <Card
              key={persona.label}
              className="group border-mocha-100 hover:border-ruby-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg min-h-[320px] flex flex-col"
            >
              <CardContent className="p-6 md:p-8 flex flex-col flex-1">
                <div className="w-12 h-12 rounded-xl bg-ruby-50 flex items-center justify-center mb-5 group-hover:bg-ruby-100 transition-colors">
                  <persona.icon
                    className="w-6 h-6 text-ruby-600"
                    aria-hidden="true"
                  />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-mocha-400 mb-2">
                  {persona.label}
                </span>
                <h3 className="font-serif text-xl font-semibold text-mocha-950 mb-3">
                  {persona.painPoint}
                </h3>
                <p className="text-mocha-600 leading-relaxed flex-1">
                  {persona.zeroEntryAngle}
                </p>
                <a
                  href="#features"
                  className="inline-flex items-center gap-1 text-xs font-medium text-ruby-600 mt-5 min-h-[48px] min-w-[48px] hover:text-ruby-700 transition-colors"
                >
                  Scopri di più
                  <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                </a>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* ---- BENCHMARK NUMBERS STRIP ---- */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-20">
          {benchmarks.map((bm) => {
            const content = (
              <div
                key={bm.label || bm.unit}
                className="bg-mocha-950 rounded-xl p-8 text-center"
              >
                <div className="font-serif text-3xl md:text-4xl font-bold text-honey-50 leading-none mb-2">
                  <AnimatedCounter
                    target={bm.value}
                    prefix={bm.prefix ?? ""}
                    suffix={bm.unit ? ` ${bm.unit}` : ""}
                    decimals={bm.decimals}
                  />
                </div>
                <div className="text-xs font-semibold text-honey-200 uppercase tracking-wide mb-1">
                  {bm.label}
                </div>
                <div className="text-[10px] text-mocha-400 mt-2">
                  Dato medio cliente KennyDB
                </div>
              </div>
            );

            if ("href" in bm && bm.href) {
              return (
                <a key={bm.label} href={bm.href} className="block hover:opacity-90 transition-opacity">
                  {content}
                </a>
              );
            }
            return content;
          })}
        </div>

        {/* ---- CTA ---- */}
        <div className="text-center mt-16">
          <Link
            href="/cta?type=trial&source=zero-entry"
            onClick={handleCTAClick}
          >
            <Button
              size="lg"
              className="bg-ruby-600 hover:bg-ruby-500 text-white px-8 py-6 text-lg font-semibold shadow-xl shadow-ruby-600/25 hover:shadow-ruby-600/40 transition-all duration-300"
            >
              Provalo con una foto — 14 giorni gratis
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
          <button
            onClick={() => setDemoOpen(true)}
            className="block mx-auto mt-4 text-sm text-mocha-500 hover:text-ruby-600 transition-colors underline underline-offset-4 decoration-mocha-300 hover:decoration-ruby-300"
          >
            Guarda la demo
          </button>
        </div>
      </div>

      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </section>
  );
}
